/**
 * Bloque de contenido al pie de cada herramienta: como funciona, como se usa,
 * preguntas frecuentes y enlaces a herramientas relacionadas.
 *
 * Por que existe: las paginas de herramienta renderizaban 140-230 palabras — la
 * navegacion mas un parrafo de intro — y todo lo demas era una calculadora que se
 * dibuja en el cliente. Para un revisor de AdSense y para Googlebot eso es una
 * pagina casi vacia, que es el motivo #1 de rechazo en sitios de calculadoras.
 *
 * Se monta una sola vez desde UserLayout, debajo del contenido de la pagina, y se
 * apaga sola (devuelve null) en las rutas que no tienen entrada en el catalogo. Asi
 * no hubo que tocar el JSX de 20 paginas distintas.
 *
 * Las cifras que aparecen en los textos salen de src/data/ — se calculan en
 * tool-content.js a partir de los mismos arrays que alimentan las calculadoras, no
 * estan escritas a mano. Si mañana entran artefactos nuevos, el texto acompaña.
 */
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Link from 'next/link'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/material'
import { useRouter } from 'next/router'

import { CONTENIDO_HERRAMIENTAS } from 'src/data/general/tool-content'
import GUIAS_POR_RUTA from 'src/data/general/guide-links.json'
import { claveDeRuta } from 'src/data/general/seo'
import AdSlot from 'src/@core/components/ads/AdSlot'

const jsonLd = obj => JSON.stringify(obj).replace(/</g, '\\u003c')

const Titulo = ({ children }) => (
  <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5 }}>
    {children}
  </Typography>
)

const Parrafo = ({ children }) => (
  <Typography variant='body2' component='p' sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.7 }}>
    {children}
  </Typography>
)

const ToolContent = () => {
  const router = useRouter()
  const clave = claveDeRuta(router?.asPath ?? '/')
  const c = CONTENIDO_HERRAMIENTAS[clave]

  if (!c) return null

  const guias = GUIAS_POR_RUTA[clave] ?? []

  return (
    <Grid container spacing={6} sx={{ mt: 6 }} component='section'>
      {/*
        El anuncio va entre la herramienta y el texto, no al final del texto.

        Estuvo al final, debajo de "How this tool works", y AdSense midio 9,3% de
        impresiones visibles: la gente usa la calculadora y se va antes de llegar.
        Aca queda donde la herramienta termina, que es hasta donde si se baja.

        Sigue sin ir arriba de la herramienta: en movil la empujaria fuera de la
        pantalla. Y como vive dentro de ToolContent hereda su corte: solo las 21
        rutas con texto curado lo muestran.
      */}
      <Grid size={{ xs: 12 }}>
        <AdSlot slot='pieHerramienta' sx={{ my: 0 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card sx={{ padding: '1.5rem' }}>
          <Titulo>{c.tituloComoFunciona ?? 'How this tool works'}</Titulo>
          {c.comoFunciona.map((p, i) => (
            <Parrafo key={`cf-${i}`}>{p}</Parrafo>
          ))}

          {c.pasos?.length ? (
            <>
              <Divider sx={{ marginY: '1.25rem' }} />
              <Titulo>Using it step by step</Titulo>
              <Box component='ol' sx={{ pl: 3, m: 0 }}>
                {c.pasos.map((p, i) => (
                  <Box component='li' key={`ps-${i}`} sx={{ mb: 1 }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {p}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : null}

          {c.faq?.length ? (
            <>
              <Divider sx={{ marginY: '1.25rem' }} />
              <Titulo>Frequently asked questions</Titulo>
              {c.faq.map((f, i) => (
                <Box key={`faq-${i}`} sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 600, mb: 0.5 }}>
                    {f.p}
                  </Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {f.r}
                  </Typography>
                </Box>
              ))}
            </>
          ) : null}

          {c.relacionadas?.length ? (
            <>
              <Divider sx={{ marginY: '1.25rem' }} />
              <Titulo>Related tools</Titulo>
              <Box component='ul' sx={{ pl: 3, m: 0 }}>
                {c.relacionadas.map((r, i) => (
                  <Box component='li' key={`rel-${i}`} sx={{ mb: 0.75 }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      <Link href={r.href} style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {r.titulo}
                      </Link>
                      {' — '}
                      {r.nota}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : null}

          {guias.length ? (
            <>
              <Divider sx={{ marginY: '1.25rem' }} />
              <Titulo>Guides that use this tool</Titulo>
              <Box component='ul' sx={{ pl: 3, m: 0 }}>
                {guias.map(g => (
                  <Box component='li' key={g.slug} sx={{ mb: 0.75 }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      <Link href={`/guides/${g.slug}/`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {g.titulo}
                      </Link>
                      {' — '}
                      {g.nota}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : null}

          <Divider sx={{ marginY: '1.25rem' }} />
          {/*
            Este pie tenia 31 palabras y salia en 21 paginas: 651 palabras
            identicas repartidas por el sitio. Peor, la frase de no-afiliacion
            repetia palabra por palabra la que ya emite el footer en la MISMA
            pagina, que es exactamente lo que la guia de AdSense marca como
            duplicado dentro de una pagina.

            Queda una linea que no repite nada y enlaza a donde esta la
            explicacion completa, una sola vez, en /about/#data.

            text.secondary y no text.disabled: con el alfa 0,38 de disabled este
            parrafo resolvia a #b4b2b7 sobre blanco, 2,1:1 de contraste. Es texto
            que se lee, no un control apagado, asi que le corresponde AA.
          */}
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Game data is compiled and checked by players —{' '}
            <Link href='/about/#data' style={{ color: 'inherit' }}>
              where it comes from and how to report an error
            </Link>
            .
          </Typography>
        </Card>
      </Grid>


      {/*
        FAQPage solo cuando hay preguntas de verdad en la pagina. Google exige que
        el markup refleje contenido visible; emitirlo vacio o con preguntas que no
        se ven es motivo de accion manual.
      */}
      {c.faq?.length ? (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: jsonLd({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: c.faq.map(f => ({
                '@type': 'Question',
                name: f.p,
                acceptedAnswer: { '@type': 'Answer', text: f.r }
              }))
            })
          }}
        />
      ) : null}
    </Grid>
  )
}

export default ToolContent
